/**
 * The current IANA name of each old zone name ICU still uses (2026-09-30).
 *
 * ICU follows CLDR, which kept the old names as its canonical ids: Node's and Chromium's
 * `Intl.supportedValuesOf("timeZone")` list `Asia/Calcutta`, not `Asia/Kolkata`, and a
 * formatter made with `Asia/Kolkata` reports `Asia/Calcutta` back, as does the browser's own zone
 * (`resolvedOptions().timeZone`). A person looks for "Kolkata", and a stored name should be the
 * one the IANA database uses today. So the display time zone picker offers the current names and
 * the server stores them (`displayTimeZoneSchema`); a stored old name still renders, because
 * every engine accepts both.
 *
 * Every key is a name ICU 78 lists (`__tests__/canonicalZone.test.ts` checks it, and that each
 * pair reads the same clock): the ones a picker built from `Intl` would otherwise offer. The
 * value is the IANA `backward` file's target. This is a rename table, not a "same clock" table:
 * `America/Detroit` is not an old name for `America/New_York` (that is the schedule picker's
 * `ZONE_ALIASES` in `client/src/lib/timezones.ts`, a different question).
 */
export const LEGACY_ZONE_NAMES: Readonly<Record<string, string>> = {
  "Africa/Asmera": "Africa/Asmara",
  "America/Buenos_Aires": "America/Argentina/Buenos_Aires",
  "America/Catamarca": "America/Argentina/Catamarca",
  "America/Coral_Harbour": "America/Atikokan",
  "America/Cordoba": "America/Argentina/Cordoba",
  "America/Godthab": "America/Nuuk",
  "America/Indianapolis": "America/Indiana/Indianapolis",
  "America/Jujuy": "America/Argentina/Jujuy",
  "America/Louisville": "America/Kentucky/Louisville",
  "America/Mendoza": "America/Argentina/Mendoza",
  "Asia/Calcutta": "Asia/Kolkata",
  "Asia/Katmandu": "Asia/Kathmandu",
  "Asia/Rangoon": "Asia/Yangon",
  "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Atlantic/Faeroe": "Atlantic/Faroe",
  "Europe/Kiev": "Europe/Kyiv",
  "Pacific/Enderbury": "Pacific/Kanton",
  "Pacific/Ponape": "Pacific/Pohnpei",
  "Pacific/Truk": "Pacific/Chuuk",
};

// A Map, so a name such as "constructor" never reads an object's own property.
const CURRENT_NAME = new Map(Object.entries(LEGACY_ZONE_NAMES));

/** The current IANA name of `zone`: `Asia/Calcutta` is `Asia/Kolkata`; any other name is itself. */
export function canonicalTimeZone(zone: string): string {
  return CURRENT_NAME.get(zone) ?? zone;
}

const FORMER_NAMES = new Map<string, string[]>();
for (const [legacy, current] of CURRENT_NAME) {
  FORMER_NAMES.set(current, [...(FORMER_NAMES.get(current) ?? []), legacy]);
}

/**
 * The old names of a current zone: `Asia/Kolkata` was `Asia/Calcutta`. The picker puts them in an
 * option's search text, so a person who types "Calcutta" or "Kiev" still finds the zone.
 */
export function formerZoneNames(zone: string): readonly string[] {
  return FORMER_NAMES.get(zone) ?? [];
}
